import React from 'react';
import { AlertTriangle, ArrowRight, Sparkles, Clock, Shield } from 'lucide-react';
import { useTranslatedContent } from '../../i18n/hooks/useTranslatedContent';
import Button from '../common/Button';

const WhatsNewSection = () => {
  const { t } = useTranslatedContent('whatsnew');
  const features = t('features', { returnObjects: true }) || [];

  return (
    <div className="relative py-24 overflow-hidden">
      {/* Modern gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'30\' height=\'30\' viewBox=\'0 0 30 30\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Ccircle cx=\'1.22676\' cy=\'1.22676\' r=\'1.22676\' fill=\'rgba(255,255,255,0.1)\'/%3E%3C/svg%3E')] opacity-20" />
      </div>

      {/* Animated orbs */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 -left-48 w-96 h-96 bg-blue-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob" />
        <div className="absolute top-1/3 -right-48 w-96 h-96 bg-indigo-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000" />
        <div className="absolute -bottom-48 left-1/2 w-96 h-96 bg-violet-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Alert Badge */}
        <div className="flex items-center justify-center space-x-4 mb-12">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-500/10 border border-blue-400/20 backdrop-blur-sm">
            <AlertTriangle className="h-5 w-5 text-blue-400 mr-2" />
            <span className="text-blue-200 font-medium">{t('badge.text')}</span>
            <AlertTriangle className="h-5 w-5 text-blue-400 ml-2" />
          </div>
        </div>

        <div className="bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 overflow-hidden">
          <div className="p-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500">
            <div className="bg-gray-900 rounded-2xl p-8 lg:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-4xl font-bold text-white mb-6">
                    {t('hero.title')}
                  </h2>
                  
                  <p className="text-lg text-blue-100 mb-8">
                    {t('hero.description')}
                  </p>

                  {/* Feature List */}
                  <div className="space-y-4 mb-8">
                    {Array.isArray(features) && features.map((feature, index) => (
                      <div 
                        key={index} 
                        className="flex items-center group bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:bg-white/10 transition-colors duration-300"
                      >
                        {index === 0 && (
                          <Clock className="h-5 w-5 text-blue-400 mr-3 group-hover:scale-110 transition-transform duration-300" />
                        )}
                        {index === 1 && (
                          <Sparkles className="h-5 w-5 text-blue-400 mr-3 group-hover:scale-110 transition-transform duration-300" />
                        )}
                        {index === 2 && (
                          <Shield className="h-5 w-5 text-blue-400 mr-3 group-hover:scale-110 transition-transform duration-300" />
                        )}
                        {index > 2 && (
                          <ArrowRight className="h-5 w-5 text-blue-400 mr-3 group-hover:scale-110 transition-transform duration-300" />
                        )}
                        <span className="text-blue-100 group-hover:text-white transition-colors duration-300">
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                  
                  <Button 
                    variant="primary" 
                    icon={ArrowRight}
                    className="bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500 hover:from-blue-600 hover:via-indigo-600 hover:to-violet-600 border-0 shadow-lg hover:shadow-xl"
                    onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    {t('hero.buttonText')}
                  </Button>
                </div>

                {/* Image Section */}
                <div className="relative group">
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 rounded-2xl transform rotate-3 group-hover:rotate-6 transition-transform duration-700" />
                  
                  {/* Main image */}
                  <img
                    src="https://images.unsplash.com/photo-1600267185393-e158a98703de?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80"
                    alt="Zimbra Upgrade"
                    className="relative rounded-2xl shadow-2xl transform -rotate-3 group-hover:rotate-0 transition-transform duration-700"
                  />

                  {/* Decorative elements */}
                  <div className="absolute -right-4 -top-4 w-24 h-24">
                    <div className="absolute right-0 top-0 w-3 h-3 bg-blue-400 rounded-full animate-ping" />
                    <div className="absolute right-8 top-8 w-2 h-2 bg-indigo-400 rounded-full animate-ping animation-delay-2000" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsNewSection;